/* eslint-disable @typescript-eslint/no-unused-vars */
import * as React from "react";
import { useLocation } from "react-router-dom";
import { styled, useTheme, Theme, CSSObject } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import CssBaseline from "@mui/material/CssBaseline";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ReorderIcon from "@mui/icons-material/Reorder";
import { useState, useEffect } from "react";
import {
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Tooltip,
  PopperPlacementType,
  Popper,
  ClickAwayListener,
  Fade,
  Paper,
  Switch,
  CircularProgress,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import SearchIcon from "@mui/icons-material/Search";
import InputAdornment from "@mui/material/InputAdornment";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Avatar from "@mui/material/Avatar";
import {
  selectUserData,
  selectUserMenuFiltered,
} from "../../features/userSlice";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import InboxIcon from "@mui/icons-material/MoveToInbox";

import "./Drawer.css";
import {
  ItemMenuConfigModule,
  MenuConfigModule,
  SubItemMenuConfigModule,
} from "../../data/menu/MenuModule";
import { toCamelCase } from "../../utils/helpers";
import { loginService } from "../../services/login.service";
import { stopTokenRefresh } from "../../services/api/fetch-wrapper";

const drawerWidth = 290;
const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});
const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(5)} + 20px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(5)} + 20px)`,
  },
});
const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));
interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}
interface SubMenuOpenStates {
  [key: string]: boolean;
}
const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

const capitalizeAndRemoveUnderscores = (str: string) => {
  return str
    .split("_")
    .map((word, index) => {
      if (index === 0 && word.toLowerCase() === "etl") {
        return "ETL";
      }
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
};

export default function MiniDrawer() {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [openSubMenu, setOpenSubMenu] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const [, setAnchorEl] = useState(null);
  const [, setSubMenuOpenStates] = useState<SubMenuOpenStates>({});
  const navigate = useNavigate();
  const userData = useSelector(selectUserData);

  const userMenuFiltered = useSelector(selectUserMenuFiltered);

  const handleDrawerOpen = () => {
    setOpen(true);
  };
  const handleDrawerClose = () => {
    setOpen(false);
    setOpenMenu(null);
    setSelectedItem(null);
  };

  const handleToggleMenu = (_: unknown, menuKey: string) => {
    // resetSearchSite();
    setOpen(true);
    setOpenMenu(openMenu === menuKey ? null : menuKey);
    setSelectedItem(location.pathname);

    // setSelectedItemInLocalStorage(menuKey);
  };

  const handleToggleSubMenu = (subMenuKey: string) => {
    // resetSearchSite();
    setSelectedItem(location.pathname);
    if (!openMenu) {
      setOpen(true);
      setOpenMenu(openSubMenu === subMenuKey ? null : subMenuKey);
    }
    setOpenSubMenu(openSubMenu === subMenuKey ? null : subMenuKey);
    setSubMenuOpenStates((prevState) => ({
      ...prevState,
      [subMenuKey]: !prevState[subMenuKey],
    }));
  };

  const location = useLocation();

  useEffect(() => {
    const isRootPath = location.pathname === "/";
    const currentPath = location.pathname;

    const normalizedPath = toCamelCase(currentPath.replace(/^\//, ""));

    if (isRootPath) {
      setSelectedItem(null);
    } else {
      setSelectedItem(currentPath);
    }

    if (!open) return; // Skip if drawer is closed

    const parentMenu = userMenuFiltered?.find((item: any) => {
      const normalizedLabel = toCamelCase(item?.label || "");
      return normalizedPath.includes(normalizedLabel);
    });

    if (parentMenu && parentMenu.label !== openMenu) {
      setOpenMenu(parentMenu.label);
    }

    const matchedSubmenu = (parentMenu?.children as any)?.find((child: any) =>
      currentPath.includes(child.label)
    );

    if (matchedSubmenu && matchedSubmenu.label !== openSubMenu) {
      setOpenSubMenu(matchedSubmenu.label);
    }

    // eslint-disable-next-line
  }, [location.pathname, open]);

  const checkedSelectedParentMenu = (label: string) => {
    if (selectedItem !== null) {
      const normalizedLabel = toCamelCase(label || "");
      const normalizedPath = toCamelCase(selectedItem.replace(/^\//, ""));
      return normalizedPath.includes(normalizedLabel);
    }
  };

  // account popover

  const [anchorElaccount, setAnchorElaccount] =
    useState<HTMLButtonElement | null>(null);
  const [openaccount, setOpenaccount] = useState(false);
  const [placementaccount, setPlacementaccount] =
    useState<PopperPlacementType>();
  const handleClickaccount =
    (newPlacement: PopperPlacementType) =>
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorElaccount(event.currentTarget);
      setOpenaccount((prev) => placement !== newPlacement || !prev);
      setPlacementaccount(newPlacement);
      setOpenpop(false);
    };
  const handleClickAwayaccount = () => {
    setOpenaccount(false);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  const handleLogout = async () => {
    try {
      setIsLoggingOut(true); // start loader
      await loginService.logout();
      handleMenuClose();
      stopTokenRefresh();
      navigate("/logout");
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      setIsLoggingOut(false); // stop loader
    }
  };

  const loggedInUserDetails = Object.values(
    userData?.data?.user_details as Record<string, any>
  );
  const [anchorElpop, setAnchorElpop] = useState<HTMLButtonElement | null>(
    null
  );
  const [openpop, setOpenpop] = useState(false);
  const [placement, setPlacement] = useState<PopperPlacementType>();

  const handleClick =
    (newPlacement: PopperPlacementType) =>
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorElpop(event.currentTarget);
      setOpenpop((prev) => placement !== newPlacement || !prev);
      setPlacement(newPlacement);
      setOpenaccount(false);
    };

  const handleClickAway = () => {
    setOpenpop(false);
  };

  // notification switch

  const [checked, setChecked] = useState(true);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(event.target.checked);
  };

  // mark all read

  const [notificationsRead, setNotificationsRead] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleMarkAllRead = () => {
    setNotificationsRead(true);
  };

  // color change when i click a notification

  const [activeNotification, setActiveNotification] = useState(null);

  const handleNotificationClick = (index: any) => {
    // Toggle the active state of the clicked notification
    setActiveNotification(activeNotification === index ? null : index);
  };
  const handleDisableDefaultBehavior = (event: any, item: any) => {
    if (location.pathname.includes(item?.path)) {
      event.preventDefault();
    }
  };

  return (
    <React.Fragment>
      <Box sx={{ display: "flex" }}>
        <Popper
          sx={{ zIndex: 999999999 }}
          open={openpop}
          anchorEl={anchorElpop}
          placement={placement}
          transition
        >
          {({ TransitionProps }) => (
            <ClickAwayListener onClickAway={handleClickAway}>
              <Fade {...TransitionProps} timeout={350}>
                <Paper>
                  <div className="notification-bg">
                    <div className="notification-header">
                      <Typography
                        sx={{
                          fontWeight: "400",
                          fontSize: "24px",
                          lineHeight: "32px",
                        }}
                      >
                        Notifications
                      </Typography>
                      <div className="notification-switch">
                        <Typography
                          sx={{
                            fontWeight: "400",
                            fontSize: "16px",
                            lineHeight: "24px",
                            letterSpacing: "0.15px",
                          }}
                        >
                          Show unread only
                        </Typography>
                        <Switch
                          checked={checked}
                          onChange={handleChange}
                          inputProps={{ "aria-label": "controlled" }}
                          color="secondary"
                        />
                        <OpenInNewIcon
                          color="secondary"
                          sx={{ marginLeft: "16px" }}
                        />
                      </div>
                    </div>
                    <Divider />
                    <div className="notification-body">
                      <div className="notification-body-header">
                        <Typography
                          sx={{
                            fontWeight: "500",
                            fontSize: "20px",
                            lineHeight: "32px",
                            letterSpacing: "0.15px",
                          }}
                        >
                          Today
                        </Typography>
                        <Typography
                          onClick={handleMarkAllRead}
                          color="secondary"
                          sx={{
                            fontWeight: "500",
                            fontSize: "16px",
                            lineHeight: "22px",
                            letterSpacing: "0.46px",
                            cursor: "pointer",
                          }}
                        >
                          Mark all as read
                        </Typography>
                      </div>
                      <div
                        className={`notification-body-details ${
                          activeNotification === 0 ? "active" : ""
                        }`}
                        onClick={() => handleNotificationClick(0)}
                      >
                        <Avatar alt="Travis Howard" src="#" />
                        <div className="NotificationShort">
                          <Typography
                            sx={{
                              fontWeight: "400",
                              fontSize: "16px",
                              lineHeight: "24px",
                              letterSpacing: "0.15px",
                            }}
                          >
                            Aladdin System Permissions updated
                          </Typography>
                          <Typography
                            sx={{
                              fontWeight: "400",
                              fontSize: "14px",
                              lineHeight: "20px",
                              letterSpacing: "0.17px",
                              color: "#00000099",
                            }}
                          >
                            Super User “Moshe” deleted
                          </Typography>
                          <Typography
                            sx={{
                              fontWeight: "400",
                              fontSize: "10px",
                              lineHeight: "14px",
                              color: "#00000099",
                            }}
                          >
                            2 Hours ago
                          </Typography>
                        </div>
                        {!notificationsRead && (
                          <FiberManualRecordIcon
                            color="secondary"
                            sx={{ width: "8px" }}
                          />
                        )}
                      </div>
                      <div className="notification-body-details">
                        <Avatar alt="Travis Howard" src="#" />
                        <div className="NotificationShort">
                          <Typography
                            sx={{
                              fontWeight: "400",
                              fontSize: "16px",
                              lineHeight: "24px",
                              letterSpacing: "0.15px",
                            }}
                          >
                            Aladdin System Permissions updated
                          </Typography>
                          <Typography
                            sx={{
                              fontWeight: "400",
                              fontSize: "14px",
                              lineHeight: "20px",
                              letterSpacing: "0.17px",
                              color: "#00000099",
                            }}
                          >
                            Super User “Moshe” deleted
                          </Typography>
                          <Typography
                            sx={{
                              fontWeight: "400",
                              fontSize: "10px",
                              lineHeight: "14px",
                              color: "#00000099",
                            }}
                          >
                            2 Hours ago
                          </Typography>
                        </div>
                        {!notificationsRead && (
                          <FiberManualRecordIcon
                            color="secondary"
                            sx={{ width: "8px" }}
                          />
                        )}
                      </div>
                      <div className="notification-body-header">
                        <Typography
                          sx={{
                            fontWeight: "500",
                            fontSize: "20px",
                            lineHeight: "32px",
                            letterSpacing: "0.15px",
                          }}
                        >
                          Yesterday
                        </Typography>
                      </div>
                      <div className="notification-body-details">
                        <Avatar alt="Travis Howard" src="#" />
                        <div className="NotificationShort">
                          <Typography
                            sx={{
                              fontWeight: "400",
                              fontSize: "16px",
                              lineHeight: "24px",
                              letterSpacing: "0.15px",
                            }}
                          >
                            Aladdin System Permissions updated
                          </Typography>
                          <Typography
                            sx={{
                              fontWeight: "400",
                              fontSize: "14px",
                              lineHeight: "20px",
                              letterSpacing: "0.17px",
                              color: "#00000099",
                            }}
                          >
                            Super User “Moshe” deleted
                          </Typography>
                          <Typography
                            sx={{
                              fontWeight: "400",
                              fontSize: "10px",
                              lineHeight: "14px",
                              color: "#00000099",
                            }}
                          >
                            2 Hours ago
                          </Typography>
                        </div>
                        {!notificationsRead && (
                          <FiberManualRecordIcon
                            color="secondary"
                            sx={{ width: "8px" }}
                          />
                        )}
                      </div>
                      <div className="notification-body-details">
                        <Avatar alt="Travis Howard" src="#" />
                        <div className="NotificationShort">
                          <Typography
                            sx={{
                              fontWeight: "400",
                              fontSize: "16px",
                              lineHeight: "24px",
                              letterSpacing: "0.15px",
                            }}
                          >
                            Aladdin System Permissions updated
                          </Typography>
                          <Typography
                            sx={{
                              fontWeight: "400",
                              fontSize: "14px",
                              lineHeight: "20px",
                              letterSpacing: "0.17px",
                              color: "#00000099",
                            }}
                          >
                            Super User “Moshe” deleted
                          </Typography>
                          <Typography
                            sx={{
                              fontWeight: "400",
                              fontSize: "10px",
                              lineHeight: "14px",
                              color: "#00000099",
                            }}
                          >
                            2 Hours ago
                          </Typography>
                        </div>
                        {!notificationsRead && (
                          <FiberManualRecordIcon
                            color="secondary"
                            sx={{ width: "8px" }}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </Paper>
              </Fade>
            </ClickAwayListener>
          )}
        </Popper>
        <Popper
          sx={{ zIndex: 999999999 }}
          open={openaccount}
          anchorEl={anchorElaccount}
          placement={placementaccount}
          transition
        >
          {({ TransitionProps }) => (
            <ClickAwayListener onClickAway={handleClickAwayaccount}>
              <Fade {...TransitionProps} timeout={350}>
                <Paper>
                  <div className="account-details-bg">
                    <Typography
                      sx={{
                        fontWeight: "500",
                        fontSize: "20px",
                        lineHeight: "32px",
                        letterSpacing: "0.15px",
                        padding: "8px 16px",
                      }}
                    >
                      Account
                    </Typography>
                    <div className="account-details">
                      <Avatar sx={{ height: "40px", width: "40px" }}>
                        {loggedInUserDetails[1].charAt(0)}
                      </Avatar>
                      <div>
                        <Typography
                          sx={{
                            fontWeight: "400",
                            fontSize: "16px",
                            lineHeight: "24px",
                            letterSpacing: "0.15px",
                          }}
                        >
                          {loggedInUserDetails[1]}
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: "400",
                            fontSize: "14px",
                            lineHeight: "20px",
                            letterSpacing: "0.15px",
                            color: "#00000099",
                          }}
                        >
                          {loggedInUserDetails[3]}
                        </Typography>
                      </div>
                    </div>
                    <Divider />
                    <div className="account-details-logout">
                      <Typography
                        sx={{
                          fontWeight: "400",
                          fontSize: "16px",
                          lineHeight: "24px",
                          letterSpacing: "0.15px",
                          paddingBlock: "8px",
                        }}
                      >
                        Profile
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: "400",
                          fontSize: "16px",
                          lineHeight: "24px",
                          letterSpacing: "0.15px",
                          paddingBlock: "8px",
                        }}
                      >
                        Settings
                      </Typography>
                      <Typography
                        onClick={!isLoggingOut ? handleLogout : undefined}
                        sx={{
                          fontWeight: "400",
                          fontSize: "16px",
                          lineHeight: "24px",
                          letterSpacing: "0.15px",
                          paddingBlock: "8px",
                          cursor: isLoggingOut ? "not-allowed" : "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        {isLoggingOut ? (
                          <>
                            <CircularProgress size={18} color="secondary" />
                            Logging out...
                          </>
                        ) : (
                          "Log out"
                        )}
                      </Typography>
                    </div>
                  </div>
                </Paper>
              </Fade>
            </ClickAwayListener>
          )}
        </Popper>
        <CssBaseline />
        <AppBar
          key={"appBar"}
          position="fixed"
          open={open}
          sx={{ background: "#FFFFFF" }}
        >
          <Toolbar
            sx={{
              display: "flex",
              justifyContent: "space-between",
              padding: "4px",
              margin: "0px, 16px, 0px, 12px",
            }}
          >
            <div className="icon-heading">
              <IconButton
                color="inherit"
                aria-label="open drawer"
                onClick={handleDrawerOpen}
                edge="start"
                sx={{
                  ...(open && { display: "none" }),
                  color: "#000000DE",
                }}
              >
                <ReorderIcon />
              </IconButton>
              <Link to="/" style={{ textDecoration: "none" }}>
                <Typography
                  variant="h6"
                  noWrap
                  component="div"
                  sx={{
                    fontFamily: "Roboto",
                    fontSize: "20px",
                    lineHeight: "32px",
                    fontStyle: "normal",
                    letterSpacing: "0.15px",
                    fontWeight: 500,
                    color: "#000000DE",
                  }}
                >
                  Aladdin
                </Typography>
              </Link>
            </div>
            <div className="textField-notification-account">
              <TextField
                placeholder="Brand, Action or keyword..."
                label="Search"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                size="small"
                color="secondary"
                sx={{ width: "250px" }}
              />
              <IconButton
                sx={{
                  color: openpop ? "#9C27B0" : "#0000008F",
                  height: "32px",
                  width: "32px",
                }}
                onClick={handleClick("bottom-end")}
              >
                <NotificationsIcon sx={{ height: "24px", width: "24px" }} />
              </IconButton>
              <IconButton
                sx={{
                  color: openpop ? "#9C27B0" : "#0000008F",
                  height: "32px",
                  width: "32px",
                }}
                onClick={handleClickaccount("bottom-end")}
              >
                <Avatar sx={{ height: "32px", width: "32px" }}>
                  {loggedInUserDetails[1].charAt(0)}
                </Avatar>
              </IconButton>
            </div>
          </Toolbar>
        </AppBar>
        <Drawer
          className="DrawerWrap"
          variant="permanent"
          open={open}
          onClose={handleDrawerClose}
        >
          <DrawerHeader>
            <IconButton onClick={handleDrawerClose}>
              {theme.direction === "rtl" ? (
                <ChevronRightIcon />
              ) : (
                <ChevronLeftIcon />
              )}
            </IconButton>
          </DrawerHeader>
          <Divider />
          <div
            className="lists"
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              height: "100%",
            }}
          >
            <div className="list1">
              <List sx={{ margin: open ? "0 8px 0 8px" : "0px 8px 0 8px" }}>
                {userMenuFiltered
                  ?.filter((key) => key?.label !== "admin") // Filter out the 'admin' key
                  .map((key: MenuConfigModule, index: number) => (
                    <React.Fragment key={index}>
                      <ListItem
                        disablePadding
                        sx={{
                          display: "block",
                          "&:hover": {
                            background: "#9C27B014",
                            color: "#9C27B0",
                          },
                          color: "#000000de",
                        }}
                        onClick={(event) => handleToggleMenu(event, key?.label)}
                      >
                        <Tooltip
                          title={capitalizeAndRemoveUnderscores(key?.label)}
                          placement="right-start"
                          disableHoverListener={open}
                        >
                          <ListItemButton
                            sx={{
                              minHeight: 48,
                              justifyContent: open ? "initial" : "center",
                              px: 2.5,
                              backgroundColor:
                                !open && checkedSelectedParentMenu(key?.label)
                                  ? "#9C27B014"
                                  : "#FFFFFF",

                              ":hover": { backgroundColor: "#9C27B014" },
                              borderRadius: "8px",
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                minWidth: 0,
                                mr: open ? 3 : "auto",
                                justifyContent: "center",
                                "&:hover": { color: "#9C27B0" },
                              }}
                            >
                              {key?.icon || <InboxIcon />}
                            </ListItemIcon>
                            <ListItemText
                              primary={capitalizeAndRemoveUnderscores(
                                key?.label
                              )}
                              sx={{ opacity: open ? 1 : 0 }}
                            />
                            <IconButton
                              edge="end"
                              sx={{
                                display: open ? "flex" : "none",
                                marginLeft: "auto",
                              }}
                            >
                              <ExpandMoreIcon
                                sx={{
                                  transform:
                                    openMenu === key?.label
                                      ? "rotate(180deg)"
                                      : "rotate(0deg)",
                                  transition: "transform 0.3s ease",
                                }}
                              />
                            </IconButton>
                          </ListItemButton>
                        </Tooltip>
                      </ListItem>
                      {openMenu === key?.label && (
                        <List
                          sx={{
                            margin: open ? "0 8px 0 8px" : "0px 8px 0 8px",
                          }}
                        >
                          {(key?.children as any)?.map(
                            (
                              subMenuItem: ItemMenuConfigModule,
                              subIndex: number
                            ) => (
                              <React.Fragment key={subIndex}>
                                <div className="subMenuWrap">
                                  {subMenuItem?.children ? (
                                    <React.Fragment key={subIndex}>
                                      <ListItem
                                        disablePadding
                                        sx={{
                                          display: "flex",
                                          background:
                                            selectedItem === subMenuItem?.label
                                              ? "#9C27B014"
                                              : "transparent",
                                          borderRadius:
                                            selectedItem === subMenuItem?.label
                                              ? "4px"
                                              : "none",
                                        }}
                                        onClick={() =>
                                          handleToggleSubMenu(
                                            subMenuItem?.label
                                          )
                                        }
                                      >
                                        <ListItemButton
                                          sx={{
                                            minHeight: 48,
                                            paddingLeft: "60px",
                                            justifyContent: open
                                              ? "initial"
                                              : "center",
                                            ":hover": {
                                              backgroundColor: "#9C27B014",
                                            },
                                            borderRadius: "8px",
                                          }}
                                        >
                                          <ListItemText
                                            primary={capitalizeAndRemoveUnderscores(
                                              subMenuItem?.label
                                            )}
                                            primaryTypographyProps={{
                                              fontSize: "14px",
                                            }}
                                            sx={{ opacity: open ? 1 : 0 }}
                                          />
                                          <IconButton
                                            edge="end"
                                            sx={{
                                              display: open ? "flex" : "none",
                                              marginLeft: "auto",
                                            }}
                                          >
                                            <ExpandMoreIcon
                                              sx={{
                                                transform:
                                                  openSubMenu ===
                                                  subMenuItem?.label
                                                    ? "rotate(180deg)"
                                                    : "rotate(0deg)",
                                                transition:
                                                  "transform 0.3s ease",
                                              }}
                                            />
                                          </IconButton>
                                        </ListItemButton>
                                      </ListItem>

                                      {open &&
                                        openSubMenu === subMenuItem?.label && (
                                          <Collapse
                                            in={
                                              openSubMenu === subMenuItem?.label
                                            }
                                            timeout="auto"
                                            unmountOnExit
                                          >
                                            <List
                                              sx={{
                                                margin: open
                                                  ? "0 8px 0 8px"
                                                  : "0px 8px 0 8px",
                                              }}
                                            >
                                              {(
                                                subMenuItem.children as any
                                              )?.map(
                                                (
                                                  subSubMenuItem: SubItemMenuConfigModule,
                                                  subSubSubIndex: number
                                                ) => (
                                                  <React.Fragment
                                                    key={subSubSubIndex}
                                                  >
                                                    <Link
                                                      data-tracker="1"
                                                      to={
                                                        subSubMenuItem?.path ||
                                                        "#"
                                                      }
                                                      onClick={(event) =>
                                                        handleDisableDefaultBehavior(
                                                          event,
                                                          subSubMenuItem
                                                        )
                                                      }
                                                      style={{
                                                        textDecoration: "none",
                                                        color: "unset",
                                                      }}
                                                    >
                                                      <ListItem
                                                        key={subSubSubIndex}
                                                        disablePadding
                                                        sx={{
                                                          display: "block",
                                                        }}
                                                      >
                                                        <ListItemButton
                                                          sx={{
                                                            minHeight: 48,
                                                            paddingLeft: "0px",
                                                            backgroundColor:
                                                              selectedItem?.includes(
                                                                subSubMenuItem?.path
                                                              )
                                                                ? "#9C27B014" //khoj
                                                                : "trnsparent",
                                                            justifyContent: open
                                                              ? "initial"
                                                              : "center",
                                                            ":hover": {
                                                              backgroundColor:
                                                                "#9C27B014",
                                                            },
                                                            borderRadius: "8px",
                                                          }}
                                                        >
                                                          <ListItemIcon
                                                            sx={{
                                                              minWidth: 0,
                                                              marginRight:
                                                                "32px",
                                                              marginLeft: "8px",
                                                              justifyContent:
                                                                "center",
                                                              "&:hover": {
                                                                color:
                                                                  "#9C27B0",
                                                              },
                                                              opacity: open
                                                                ? 1
                                                                : 0,
                                                              color: "#9C27B0",
                                                            }}
                                                          >
                                                            <ArrowRightIcon />
                                                          </ListItemIcon>
                                                          <ListItemText
                                                            sx={{
                                                              whiteSpace:
                                                                "pre-line",
                                                            }}
                                                            primary={capitalizeAndRemoveUnderscores(
                                                              subSubMenuItem.label
                                                            )}
                                                            primaryTypographyProps={{
                                                              fontSize: "14px",
                                                            }}
                                                          />
                                                        </ListItemButton>
                                                      </ListItem>
                                                    </Link>
                                                  </React.Fragment>
                                                )
                                              )}
                                            </List>
                                          </Collapse>
                                        )}
                                    </React.Fragment>
                                  ) : (
                                    <List
                                      sx={{
                                        margin: open
                                          ? "0 8px 0 8px"
                                          : "0px 8px 0 8px",
                                      }}
                                    >
                                      <React.Fragment key={subIndex}>
                                        <Link
                                          data-tracker="1"
                                          to={subMenuItem?.path || "#"}
                                          style={{
                                            textDecoration: "none",
                                            color: "unset",
                                          }}
                                          onClick={(event) =>
                                            handleDisableDefaultBehavior(
                                              event,
                                              subMenuItem
                                            )
                                          }
                                        >
                                          <ListItem
                                            key={subIndex}
                                            disablePadding
                                            sx={{
                                              display: "block",
                                            }}
                                          >
                                            <ListItemButton
                                              sx={{
                                                minHeight: 48,
                                                paddingLeft: "0px",
                                                backgroundColor:
                                                  selectedItem?.includes(
                                                    subMenuItem?.path
                                                  )
                                                    ? "#9C27B014" //khoj
                                                    : "trnsparent",
                                                justifyContent: open
                                                  ? "initial"
                                                  : "center",
                                                ":hover": {
                                                  backgroundColor: "#9C27B014",
                                                },
                                                borderRadius: "8px",
                                              }}
                                            >
                                              <ListItemIcon
                                                sx={{
                                                  minWidth: 0,
                                                  marginRight: "32px",
                                                  marginLeft: "8px",
                                                  justifyContent: "center",
                                                  "&:hover": {
                                                    color: "#9C27B0",
                                                  },
                                                  opacity: open ? 1 : 0,
                                                  color: "#9C27B0",
                                                }}
                                              >
                                                <ArrowRightIcon />
                                              </ListItemIcon>
                                              <ListItemText
                                                primary={capitalizeAndRemoveUnderscores(
                                                  subMenuItem?.label
                                                )}
                                                primaryTypographyProps={{
                                                  fontSize: "14px",
                                                }}
                                              />
                                            </ListItemButton>
                                          </ListItem>
                                        </Link>
                                      </React.Fragment>
                                    </List>
                                  )}
                                </div>
                              </React.Fragment>
                            )
                          )}
                        </List>
                      )}
                    </React.Fragment>
                  ))}
              </List>
            </div>
            {(
              userMenuFiltered?.filter(
                (menu: MenuConfigModule) => menu?.label === "admin"
              ) as any
            ).length === 1 ? (
              <div className="list2">
                <List sx={{ margin: open ? "0 8px 0 8px" : "0px 8px 0 8px" }}>
                  <ListItem
                    disablePadding
                    sx={{ display: "block" }}
                    onClick={(event) => handleToggleMenu(event, "admin")}
                  >
                    <ListItemButton
                      sx={{
                        minHeight: 48,
                        justifyContent: open ? "initial" : "center",
                        px: 2.5,
                        backgroundColor:
                          !open && checkedSelectedParentMenu("admin")
                            ? "#9C27B014"
                            : "#FFFFFF",
                        ":hover": { backgroundColor: "#9C27B014" },
                        borderRadius: "8px",
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: open ? 3 : "auto",
                          justifyContent: "center",
                          "&:hover": { color: "#9C27B0" },
                        }}
                      >
                        {
                          (
                            userMenuFiltered?.filter(
                              (menu: MenuConfigModule) =>
                                menu?.label === "admin"
                            ) as any
                          )[0]?.icon
                        }
                      </ListItemIcon>
                      <ListItemText
                        primary={capitalizeAndRemoveUnderscores("admin")}
                        sx={{ opacity: open ? 1 : 0 }}
                      />
                      <IconButton
                        edge="end"
                        sx={{
                          display: open ? "flex" : "none",
                          marginLeft: "auto",
                        }}
                      >
                        <ExpandMoreIcon
                          sx={{
                            transform:
                              openMenu === "admin"
                                ? "rotate(180deg)"
                                : "rotate(0deg)",
                            transition: "transform 0.3s ease",
                          }}
                        />
                      </IconButton>
                    </ListItemButton>
                  </ListItem>
                  {openMenu === "admin" && (
                    <List
                      className="subMenuWrap"
                      sx={{ margin: open ? "0 8px 0 8px" : "0px 8px 0 8px" }}
                    >
                      {userMenuFiltered?.map(
                        (subMenuItem: MenuConfigModule, subIndex: any) =>
                          subMenuItem?.label === "admin" ? (
                            <React.Fragment key={subIndex}>
                              {(subMenuItem["children"] as any)?.map(
                                (
                                  subKey: ItemMenuConfigModule,
                                  subSubIndex: number
                                ) => (
                                  <div className="subMenuWrap">
                                    {subKey?.children ? (
                                      <React.Fragment key={subSubIndex}>
                                        <ListItem
                                          disablePadding
                                          sx={{
                                            display: "flex",
                                            background:
                                              selectedItem === subKey?.label
                                                ? "#9C27B014" //Did not find anything
                                                : "transparent",
                                            borderRadius:
                                              selectedItem === subKey?.label
                                                ? "4px"
                                                : "none",
                                          }}
                                          onClick={() =>
                                            handleToggleSubMenu(subKey?.label)
                                          }
                                        >
                                          <ListItemButton
                                            sx={{
                                              minHeight: 48,
                                              paddingLeft: "60px",
                                              justifyContent: open
                                                ? "initial"
                                                : "center",
                                              ":hover": {
                                                backgroundColor: "#9C27B014", // For admin sub not sub sub khoj
                                              },
                                              borderRadius: "8px",
                                            }}
                                          >
                                            <ListItemText
                                              primary={capitalizeAndRemoveUnderscores(
                                                subKey?.label
                                              )}
                                              primaryTypographyProps={{
                                                fontSize: "14px",
                                              }}
                                              sx={{ opacity: open ? 1 : 0 }}
                                            />
                                            <IconButton
                                              edge="end"
                                              sx={{
                                                display: open ? "flex" : "none",
                                                marginLeft: "auto",
                                              }}
                                            >
                                              <ExpandMoreIcon
                                                sx={{
                                                  transform:
                                                    openSubMenu ===
                                                    subKey?.label
                                                      ? "rotate(180deg)"
                                                      : "rotate(0deg)",
                                                  transition:
                                                    "transform 0.3s ease",
                                                }}
                                              />
                                            </IconButton>
                                          </ListItemButton>
                                        </ListItem>
                                        {openSubMenu === subKey?.label && (
                                          <List
                                            sx={{
                                              margin: open
                                                ? "0 8px 0 8px"
                                                : "0px 8px 0 8px",
                                            }}
                                          >
                                            {(subKey?.children as any)?.map(
                                              (
                                                subSubMenuItem: SubItemMenuConfigModule,
                                                subSubSubIndex: any
                                              ) => (
                                                <React.Fragment
                                                  key={subSubSubIndex}
                                                >
                                                  <Link
                                                    data-tracker="1"
                                                    to={
                                                      subSubMenuItem?.path ||
                                                      "#"
                                                    }
                                                    style={{
                                                      textDecoration: "none",
                                                      color: "unset",
                                                    }}
                                                    onClick={(event) =>
                                                      handleDisableDefaultBehavior(
                                                        event,
                                                        subSubMenuItem
                                                      )
                                                    }
                                                  >
                                                    <ListItem
                                                      key={subSubSubIndex}
                                                      disablePadding
                                                      sx={{ display: "block" }}
                                                    >
                                                      <ListItemButton
                                                        sx={{
                                                          minHeight: 48,
                                                          paddingLeft: "0px",
                                                          backgroundColor:
                                                            subSubMenuItem?.path ===
                                                            selectedItem
                                                              ? "#9C27B014"
                                                              : "transparent",
                                                          justifyContent: open
                                                            ? "initial"
                                                            : "center",
                                                          ":hover": {
                                                            backgroundColor:
                                                              "#9C27B014", //Fot Process Khoj
                                                          },
                                                          borderRadius: "8px",
                                                        }}
                                                      >
                                                        <ListItemIcon
                                                          sx={{
                                                            minWidth: 0,
                                                            marginRight: "32px",
                                                            marginLeft: "8px",
                                                            justifyContent:
                                                              "center",
                                                            "&:hover": {
                                                              color: "#9C27B0",
                                                            },
                                                            opacity: open
                                                              ? 1
                                                              : 0,
                                                            color: "#9C27B0",
                                                          }}
                                                        >
                                                          <ArrowRightIcon />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                          sx={{
                                                            whiteSpace:
                                                              "pre-line",
                                                          }}
                                                          primary={capitalizeAndRemoveUnderscores(
                                                            subSubMenuItem?.label
                                                          )}
                                                          primaryTypographyProps={{
                                                            fontSize: "14px",
                                                          }}
                                                        />
                                                      </ListItemButton>
                                                    </ListItem>
                                                  </Link>
                                                </React.Fragment>
                                              )
                                            )}
                                          </List>
                                        )}
                                      </React.Fragment>
                                    ) : (
                                      <List
                                        sx={{
                                          margin: open
                                            ? "0 8px 0 8px"
                                            : "0px 8px 0 8px",
                                        }}
                                      >
                                        <React.Fragment key={subSubIndex}>
                                          <Link
                                            data-tracker="1"
                                            to={subKey?.path || "#"}
                                            style={{
                                              textDecoration: "none",
                                              color: "unset",
                                            }}
                                            onClick={(event) =>
                                              handleDisableDefaultBehavior(
                                                event,
                                                subKey
                                              )
                                            }
                                          >
                                            <ListItem
                                              key={subSubIndex}
                                              disablePadding
                                              sx={{ display: "block" }}
                                            >
                                              <ListItemButton
                                                sx={{
                                                  minHeight: 48,
                                                  paddingLeft: "0px",
                                                  backgroundColor:
                                                    subKey?.path ===
                                                    selectedItem
                                                      ? "#9C27B014"
                                                      : "transparent",
                                                  justifyContent: open
                                                    ? "initial"
                                                    : "center",
                                                  ":hover": {
                                                    backgroundColor:
                                                      "#9C27B014", //For meta entity and Datastore khoj
                                                  },
                                                  borderRadius: "8px",
                                                }}
                                              >
                                                <ListItemIcon
                                                  sx={{
                                                    minWidth: 0,
                                                    marginRight: "32px",
                                                    marginLeft: "8px",
                                                    justifyContent: "center",
                                                    "&:hover": {
                                                      color: "#9C27B0",
                                                    },
                                                    opacity: open ? 1 : 0,
                                                    color: "#9C27B0",
                                                  }}
                                                >
                                                  <ArrowRightIcon />
                                                </ListItemIcon>
                                                <ListItemText
                                                  sx={{}}
                                                  primary={capitalizeAndRemoveUnderscores(
                                                    subKey?.label
                                                  )}
                                                  primaryTypographyProps={{
                                                    fontSize: "14px",
                                                  }}
                                                />
                                              </ListItemButton>
                                            </ListItem>
                                          </Link>
                                        </React.Fragment>
                                      </List>
                                    )}
                                  </div>
                                )
                              )}
                            </React.Fragment>
                          ) : (
                            ""
                          )
                      )}
                    </List>
                  )}
                </List>
                <Typography
                  sx={{
                    minHeight: 48,
                    fontSize: "0.9rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#00000099",
                    opacity: open ? 1 : 0,
                  }}
                >
                  Powered by Optival LTD
                </Typography>
              </div>
            ) : (
              ""
            )}
          </div>
        </Drawer>
      </Box>
    </React.Fragment>
  );
}
